import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { Slot } from "radix-ui"

import { cn } from "@/lib/utils"

/**
 * 禁用态不用 `opacity-50`。
 *
 * 半透明会把前景往背景里洗，浅色底上尤其明显：品牌粉 50% 压在白底上是淡粉，
 * 再叠 50% 的白字，对比度掉到 2:1 上下——「已保存」这种状态文案直接读不出来。
 * 改成中性灰底 + 三级灰字：仍然一眼看得出不可点（无 hover、无阴影、失去品牌色），
 * 但字是清楚的。--muted / --mut2 两个主题都有取值，这里不需要分主题写。
 */
const DISABLED_FILLED = "disabled:bg-muted disabled:text-mut2 disabled:shadow-none"

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default: `bg-primary text-primary-foreground hover:bg-primary/90 ${DISABLED_FILLED}`,
        destructive: `bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:bg-destructive/60 dark:focus-visible:ring-destructive/40 ${DISABLED_FILLED}`,
        outline:
          "border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground disabled:border-line disabled:bg-muted/60 disabled:text-mut2 disabled:shadow-none dark:border-input dark:bg-input/30 dark:hover:bg-input/50",
        secondary: `bg-secondary text-secondary-foreground hover:bg-secondary/80 ${DISABLED_FILLED}`,
        ghost:
          "hover:bg-accent hover:text-accent-foreground disabled:text-mut2 dark:hover:bg-accent/50",
        link: "text-primary underline-offset-4 hover:underline disabled:text-mut2 disabled:no-underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        xs: "h-6 gap-1 rounded-md px-2 text-xs has-[>svg]:px-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-8 gap-1.5 rounded-md px-3 has-[>svg]:px-2.5",
        lg: "h-10 rounded-md px-6 has-[>svg]:px-4",
        icon: "size-9",
        "icon-xs": "size-6 rounded-md [&_svg:not([class*='size-'])]:size-3",
        "icon-sm": "size-8",
        "icon-lg": "size-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  ...props
}: React.ComponentProps<"button"> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean
  }) {
  const Comp = asChild ? Slot.Root : "button"

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  )
}

export { Button, buttonVariants }
